import React, { useState, useEffect } from 'react';
import { Table, Dropdown, Menu, Button, Space, Typography, Modal, message, Descriptions } from 'antd';
import { EyeOutlined, FlagOutlined, CheckOutlined, DownOutlined, DeleteOutlined } from '@ant-design/icons';
import './VerificationsProvider.css';

const { Title } = Typography;

const VerificationsProvider = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [verificationToDelete, setVerificationToDelete] = useState(null);

  // Fetch verifications from backend
  const fetchVerifications = async () => {
    setLoading(true);
    try {
      const response = await fetch('/backend/superadmin_get_verifications.php', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Erreur de parsing JSON:', e, 'Texte reçu:', text);
        throw new Error('Réponse non-JSON');
      }
      if (response.ok) {
        console.log('Données vérifications reçues:', data.verifications);
        setDataSource(data.verifications || []);
      } else {
        if (response.status === 403) {
          message.error('Session expirée. Veuillez vous reconnecter.');
        } else {
          message.error(data.error || `Erreur HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erreur de réseau:', error.message);
      message.error('Erreur réseau lors de la récupération des vérifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVerifications();
  }, []);

  // Handle view
  const handleView = (record) => {
    console.log('View:', record);
    setSelectedVerification(record);
    setIsViewModalVisible(true);
  };

  // Handle flag
  const handleFlag = async (record) => {
    console.log('Flag:', record);
    try {
      const response = await fetch('/backend/superadmin_flag_verification.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: record.id }),
      });
      const data = await response.json();
      console.log('Réponse flag:', data);
      if (response.ok) {
        message.success(data.message || 'Vérification signalée avec succès');
        fetchVerifications();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors du signalement:', error);
      message.error('Erreur réseau lors du signalement');
    }
  };

  // Handle approve
  const handleApprove = async (record) => {
    console.log('Approve:', record);
    try {
      const response = await fetch('/backend/superadmin_approve_verification.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: record.id }),
      });
      const data = await response.json();
      console.log('Réponse approve:', data);
      if (response.ok) {
        message.success(data.message || 'Vérification approuvée avec succès');
        fetchVerifications();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors de l\'approbation:', error);
      message.error('Erreur réseau lors de l\'approbation');
    }
  };

  // Handle delete
  const handleDelete = (record) => {
    console.log('handleDelete appelé pour:', record);
    setVerificationToDelete(record);
    setIsDeleteModalVisible(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!verificationToDelete) return;
    console.log('Confirmation de suppression pour ID:', verificationToDelete.id);
    try {
      const response = await fetch('/backend/superadmin_delete_verification.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: verificationToDelete.id }),
      });
      console.log('Réponse de suppression:', response.status, response.statusText);
      const data = await response.json();
      console.log('Données de réponse:', data);
      if (response.ok) {
        message.success(data.message || 'Vérification supprimée avec succès');
        setIsDeleteModalVisible(false);
        setVerificationToDelete(null);
        fetchVerifications();
      } else {
        message.error(data.error || `Erreur HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Erreur réseau lors de la suppression:', error);
      message.error('Erreur réseau lors de la suppression');
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    console.log('Suppression annulée');
    setIsDeleteModalVisible(false);
    setVerificationToDelete(null);
  };

  // Handle view modal close
  const handleViewModalClose = () => {
    setIsViewModalVisible(false);
    setSelectedVerification(null);
  };

  // Menu for dropdown actions
  const menu = (record) => (
    <Menu>
      <Menu.Item key="1" icon={<EyeOutlined />} onClick={() => handleView(record)}>
        View
      </Menu.Item>
      <Menu.Item key="2" icon={<FlagOutlined />} onClick={() => handleFlag(record)}>
        Flag
      </Menu.Item>
      <Menu.Item key="3" icon={<CheckOutlined />} onClick={() => handleApprove(record)}>
        Approve
      </Menu.Item>
      <Menu.Item key="4" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
        Delete
      </Menu.Item>
    </Menu>
  );

  // Define columns
  const columns = [
    { title: 'Email', dataIndex: 'admin_email', key: 'admin_email' },
    { title: 'Date', dataIndex: 'created_at', key: 'created_at' },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status', 
      render: (status) => (
        <span className={status === 'Verified' ? 'status-verified' : status === 'Rejected' ? 'status-rejected' : 'status-pending'}>
          {status}
        </span>
      ),
    },
    { 
      title: 'Action', 
      key: 'action', 
      render: (_, record) => (
        <Space size="middle">
          <Dropdown overlay={menu(record)} trigger={['click']}>
            <Button icon={<DownOutlined />} size="small">
              Actions
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div className="verifications-provider-container">
      <Title level={3} className="verifications-provider-title">Admin Verifications KYC</Title>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={{ pageSize: 5 }}
        className="verifications-provider-table"
        loading={loading}
      />
      {/* View Modal */}
      <Modal
        title={`Détails de la vérification - ${selectedVerification?.admin_email}`}
        open={isViewModalVisible}
        onOk={handleViewModalClose}
        onCancel={handleViewModalClose}
        okText="Fermer"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={600}
      >
        {selectedVerification && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="ID">{selectedVerification.id}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedVerification.admin_email}</Descriptions.Item>
            <Descriptions.Item label="Numéro EIN">{selectedVerification.ein_number || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Preuve d'adresse">
              {selectedVerification.proof_of_address_path ? (
                <a href={`/backend/${selectedVerification.proof_of_address_path}`} target="_blank" rel="noopener noreferrer">
                  Voir
                </a>
              ) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="ID gouvernemental">
              {selectedVerification.gov_issued_id_path ? (
                <a href={`/backend/${selectedVerification.gov_issued_id_path}`} target="_blank" rel="noopener noreferrer">
                  Voir
                </a>
              ) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Preuve d'autorité">
              {selectedVerification.proof_of_authority_path ? (
                <a href={`/backend/${selectedVerification.proof_of_authority_path}`} target="_blank" rel="noopener noreferrer">
                  Voir
                </a>
              ) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Documents supplémentaires">{selectedVerification.extra_docs || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Chemin des documents supplémentaires">
              {selectedVerification.extra_docs_path ? (
                <a href={`/backend/${selectedVerification.extra_docs_path}`} target="_blank" rel="noopener noreferrer">
                  Voir
                </a>
              ) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Statut">{selectedVerification.status}</Descriptions.Item>
            <Descriptions.Item label="Créé le">{selectedVerification.created_at}</Descriptions.Item>
            <Descriptions.Item label="Mis à jour le">{selectedVerification.updated_at || 'N/A'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirmer la suppression"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Supprimer"
        okType="danger"
        cancelText="Annuler"
      >
        <p>
          Voulez-vous vraiment supprimer la vérification pour {verificationToDelete?.admin_email} ?
        </p>
      </Modal>
    </div>
  );
};

export default VerificationsProvider;